import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { downloadBlob } from '@sequencehq/utils'
import SpinnerContainer from 'components/Loading/SpinnerContainer'
import { REVENUE_WATERFALL_PATH } from 'RevenueRecognition/revenueRecognition.router'

const DEFAULT_FILENAME = 'revenue-waterfall.csv'

const WATERFALL_REPORT_FILENAME_PATTERN =
  /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})_([A-Z]{3})_[\w-]+\.csv$/

export const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname.split('/').pop() ?? ''
    const match = filename.match(WATERFALL_REPORT_FILENAME_PATTERN)
    if (!match) {
      Sentry.captureException(
        new Error(
          `Waterfall report filename did not match expected pattern: ${filename}`
        )
      )
      return DEFAULT_FILENAME
    }
    return `revenue-waterfall-${match[1]}-to-${match[2]}-${match[3]}.csv`
  } catch (error) {
    Sentry.captureException(error)
    return DEFAULT_FILENAME
  }
}

export const WaterfallReportDownload = () => {
  const [searchParams] = useSearchParams()
  const signature = searchParams.get('signature')
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()

  const { data, isError, isSuccess } = useQuery(
    dashboardv99990101Client.getWaterfallReportSignedDownload,
    { signature: signature ?? '' },
    { enabled: !!signature }
  )

  useEffect(() => {
    if (isSuccess && data?.url) {
      ;(async () => {
        try {
          const response = await fetch(data.url)
          if (!response.ok) {
            throw new Error('Download failed')
          }

          const filename = getFilenameFromUrl(data.url)
          const blob = await response.blob()
          downloadBlob(filename)(blob)

          displayNotification('Downloaded successfully', {
            type: 'success'
          })
        } catch {
          displayNotification('Failed to download report', {
            type: 'error'
          })
        } finally {
          navigate(REVENUE_WATERFALL_PATH)
        }
      })()
    } else if (isError || !signature || (isSuccess && !data?.url)) {
      displayNotification('This download link has expired or is invalid', {
        type: 'error'
      })
      navigate(REVENUE_WATERFALL_PATH)
    }
  }, [signature, isSuccess, isError, data, displayNotification, navigate])

  return <SpinnerContainer />
}
